import React, { useEffect, useState, useCallback } from "react";
import api, { downloadBlockHistoryPdf } from "../../api";
import { toast } from "sonner";
import { Ban, ShieldCheck, Clock, Bot, User, FileDown, Search, History } from "lucide-react";

const PAGE = 50;

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

const ACTION_META = {
  block: { label: "Blocco", cls: "bg-red-100 text-red-700", icon: Ban },
  unblock: { label: "Sblocco", cls: "bg-green-100 text-green-700", icon: ShieldCheck },
  expired: { label: "Scaduto", cls: "bg-gray-200 text-gray-600", icon: Clock },
};

const selectCls = "border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-[#16345c] bg-white";

const detailOf = (it) => {
  if (it.action !== "block") return "—";
  if (it.block_type === "temporary" && it.expires_at) return `Temporaneo · fino al ${fmtDateTime(it.expires_at)}`;
  return "Permanente";
};

export const BlockHistoryTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("all");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const query = useCallback((sk) => `?limit=${PAGE}&skip=${sk}&action=${action}&source=${source}&q=${encodeURIComponent(q)}`, [action, source, q]);

  const load = useCallback((sk = 0, append = false) => {
    setLoading(true);
    api.get(`/security/block-history${query(sk)}`)
      .then((r) => {
        setTotal(r.data.total || 0);
        setSkip(sk);
        setItems((prev) => (append ? [...prev, ...(r.data.items || [])] : (r.data.items || [])));
      })
      .catch(() => toast.error("Errore nel caricamento dello storico"))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => { load(0, false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [action, source]);

  const doSearch = (e) => { e.preventDefault(); load(0, false); };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadBlockHistoryPdf(`?action=${action}&source=${source}&q=${encodeURIComponent(q)}`, "storico_blocchi.pdf");
      toast.success("Storico blocchi scaricato");
    } catch { toast.error("Errore durante il download"); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="block-history-tab">
      <div className="flex items-center gap-2 mb-4 text-[13px] text-gray-600">
        <History size={16} className="text-[#16345c] shrink-0" />
        Registro cronologico di ogni blocco, sblocco e scadenza degli indirizzi IP: chi lo ha eseguito (HR o Sistema automatico), quando e perché.
      </div>

      <form onSubmit={doSearch} className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white w-full sm:w-[260px]">
          <Search size={16} className="text-gray-400" />
          <input value={q} data-testid="block-history-search" onChange={(e) => setQ(e.target.value)} placeholder="Cerca per indirizzo IP..." className="flex-1 py-2 px-2 text-[14px] outline-none" />
        </div>
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="block-history-filter-action" className={selectCls}>
          <option value="all">Tutte le azioni</option>
          <option value="block">Solo blocchi</option>
          <option value="unblock">Solo sblocchi</option>
          <option value="expired">Solo scadenze</option>
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="block-history-filter-source" className={selectCls}>
          <option value="all">Tutte le origini</option>
          <option value="auto">Automatico</option>
          <option value="manual">Manuale (HR)</option>
        </select>
        <button type="submit" data-testid="block-history-search-btn" className="bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors">Cerca</button>
        <button type="button" onClick={exportPdf} disabled={exporting} data-testid="block-history-export-pdf" className="ml-auto flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60"><FileDown size={15} /> {exporting ? "..." : "Report PDF"}</button>
      </form>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="block-history-table">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Data / Ora</th>
              <th className="text-left px-4 py-3 font-bold">Indirizzo IP</th>
              <th className="text-left px-4 py-3 font-bold">Azione</th>
              <th className="text-left px-4 py-3 font-bold">Origine</th>
              <th className="text-left px-4 py-3 font-bold">Eseguito da</th>
              <th className="text-left px-4 py-3 font-bold">Dettaglio</th>
              <th className="text-left px-4 py-3 font-bold">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const meta = ACTION_META[it.action] || { label: it.action, cls: "bg-gray-100 text-gray-600", icon: Ban };
              const ActIcon = meta.icon;
              return (
                <tr key={it.id} data-testid={`block-history-row-${it.id}`} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-[13px] text-gray-700 whitespace-nowrap">{fmtDateTime(it.created_at)}</td>
                  <td className="px-4 py-3"><span className="font-mono text-[13px] font-bold text-[#16345c]">{it.ip}</span></td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${meta.cls}`}><ActIcon size={12} /> {meta.label}</span></td>
                  <td className="px-4 py-3">
                    {it.auto
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 text-white"><Bot size={11} /> Automatico</span>
                      : <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700"><User size={11} /> Manuale</span>}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-700">{it.by_name || (it.auto ? "Sistema (automatico)" : "—")}</td>
                  <td className="px-4 py-3 text-[12.5px] text-gray-600 whitespace-nowrap">{detailOf(it)}</td>
                  <td className="px-4 py-3 text-[12.5px] text-gray-600 max-w-[260px]">{it.reason || "—"}</td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessun evento di blocco/sblocco registrato con i filtri selezionati.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className="text-[12px] text-gray-500" data-testid="block-history-count">{items.length} di {total} eventi</span>
        {items.length < total && (
          <button onClick={() => load(skip + PAGE, true)} disabled={loading} data-testid="block-history-load-more" className="text-[13px] font-bold text-[#16345c] border border-[#16345c] hover:bg-[#16345c] hover:text-white px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
            {loading ? "Caricamento..." : "Carica altri"}
          </button>
        )}
      </div>
    </div>
  );
};
